import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  CreditCardIcon,
  EyeIcon,
  PlusIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  LightBulbIcon,
  BanknotesIcon,
  UserGroupIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../../contexts/LanguageContext';
import { smartCardsData } from '../../../data/smartCardsData';

// Interfaces are now imported from smartCardsData.ts

const SmartCardsPreview: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [selectedCard, setSelectedCard] = useState<string | null>(null);

  // Navigation handlers for different actions
  const handleCardAction = (cardType: string, cardId: string) => {
    switch (cardType) {
      case 'department':
        navigate(`/dashboard/departments/${cardId}`);
        break;
      case 'branch':
        navigate(`/dashboard/branches/${cardId}`);
        break;
      case 'project':
        navigate(`/dashboard/projects/${cardId}`);
        break;
      case 'campaign':
        navigate(`/dashboard/marketing/campaigns/${cardId}`);
        break;
      default:
        navigate('/dashboard/analytics');
    }
  };

  const handleRecommendationAction = (recommendationType: string, cardId: string) => {
    switch (recommendationType) {
      case 'optimization':
        navigate('/dashboard/optimization');
        break;
      case 'partnership':
        navigate('/dashboard/partnerships');
        break;
      case 'financing':
        navigate('/dashboard/financing');
        break;
      case 'alert':
        navigate(`/dashboard/alerts/${cardId}`);
        break;
      case 'urgent':
        navigate(`/dashboard/emergency/${cardId}`);
        break;
      default:
        navigate('/dashboard/recommendations');
    }
  };

  const handleViewAllCards = () => {
    navigate('/dashboard/smart-cards');
  };

  const handleCreateNewCard = () => {
    navigate('/dashboard/smart-cards/create');
  };

  const handleViewAllRecommendations = (cardId: string) => {
    navigate(`/dashboard/recommendations/${cardId}`);
  };

  const handleRefreshData = (cardId: string) => {
    // Future: API call to refresh card data
    console.log(`Refreshing data for card ${cardId}`);
    // For now, just show a notification or loading state
  };

  const smartCards = smartCardsData.slice(0, 3); // Show first 3 cards in preview

  // Helper functions for rendering
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-5 w-5 text-green-300" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-300" />;
      case 'critical':
        return <XCircleIcon className="h-5 w-5 text-red-300" />;
      default:
        return <CheckCircleIcon className="h-5 w-5 text-white/60" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400';
      case 'warning':
        return 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400';
      case 'critical':
        return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'department':
        return 'قسم';
      case 'branch':
        return 'فرع';
      case 'project':
        return 'مشروع';
      case 'campaign':
        return 'حملة';
      default:
        return 'عام';
    }
  };

  const getRecommendationIcon = (type: string) => {
    switch (type) {
      case 'optimization':
        return <LightBulbIcon className="h-3 w-3" />;
      case 'partnership':
        return <UserGroupIcon className="h-3 w-3" />;
      case 'financing':
        return <BanknotesIcon className="h-3 w-3" />;
      case 'alert':
        return <ExclamationTriangleIcon className="h-3 w-3" />;
      case 'urgent':
        return <XCircleIcon className="h-3 w-3" />;
      default:
        return <CheckCircleIcon className="h-3 w-3" />;
    }
  };

  const getImpactBadge = (impact: string) => {
    switch (impact) {
      case 'high':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700"
    >
      {/* Header */}
      <div className="border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
              <CreditCardIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                البطاقات الذكية
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                نظرة سريعة على أداء الأقسام والمشاريع
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <button 
              onClick={handleViewAllCards}
              className="flex items-center space-x-1 rtl:space-x-reverse px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200"
            >
              <EyeIcon className="h-4 w-4" />
              <span>عرض الكل</span>
            </button>
            
            <button 
              onClick={handleCreateNewCard}
              className="flex items-center space-x-1 rtl:space-x-reverse px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <PlusIcon className="h-4 w-4" />
              <span>بطاقة جديدة</span>
            </button>
          </div>
        </div>
      </div>

      {/* Compact Stacked Cards */}
      <div className="p-6">
        <div className="relative">
          {/* Cards Stack */}
          <div className="flex flex-col space-y-4">
            {smartCards.map((card: any, index: number) => {
              const isSelected = selectedCard === card.id;
              
              return (
                <motion.div
                  key={card.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="relative"
                >
                  {/* Compact Card */}
                  <motion.div
                    whileHover={{ scale: 1.02, x: 8 }}
                    onClick={() => setSelectedCard(isSelected ? null : card.id)}
                    onDoubleClick={() => handleCardAction(card.type, card.id)}
                    className={`relative bg-gradient-to-r ${card.color} p-4 rounded-xl shadow-lg cursor-pointer transition-all duration-300 hover:shadow-xl border border-white/20 backdrop-blur-sm`}
                  >
                    <div className="space-y-3">
                      {/* Top Row - Card Info */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 rtl:space-x-reverse flex-1">
                          {/* Card Icon & Type */}
                          <div className="flex items-center space-x-2 rtl:space-x-reverse">
                            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                              {getStatusIcon(card.status)}
                            </div>
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur-sm">
                              {getTypeLabel(card.type)}
                            </span>
                          </div>
                          
                          {/* Card Title */}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white font-bold text-base leading-tight truncate">
                              {card.title}
                            </h4>
                          </div>
                        </div>
                        
                        {/* Status Indicators */}
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          {card.alerts > 0 && (
                            <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-red-500 rounded-full">
                              {card.alerts}
                            </span>
                          )}
                          {card.recommendations > 0 && (
                            <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-amber-500 rounded-full">
                              {card.recommendations}
                            </span>
                          )}
                          
                          {/* Action Button */}
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCardAction(card.type, card.id);
                            }}
                            className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-200 backdrop-blur-sm"
                          >
                            <ArrowRightIcon className="w-4 h-4 text-white" />
                          </motion.button>
                        </div>
                      </div>
                      
                      {/* Bottom Row - Budget & Performance */}
                      <div className="flex items-center justify-between">
                        {/* Left - Last Update */}
                        <div>
                          <p className="text-white/70 text-xs">
                            آخر تحديث: {card.lastUpdate}
                          </p>
                        </div>
                        
                        {/* Center - Budget Progress */}
                        <div className="flex items-center space-x-4 rtl:space-x-reverse">
                          <div className="text-center">
                            <div className="flex items-center space-x-1 rtl:space-x-reverse mb-1">
                              <span className="text-white/80 text-xs">الاستخدام</span>
                              <span className="text-white font-bold text-sm">{card.utilization}%</span>
                            </div>
                            <div className="w-20 bg-white/25 rounded-full h-2">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${card.utilization}%` }}
                                transition={{ duration: 1, delay: index * 0.2 }}
                                className="h-2 bg-white rounded-full"
                              />
                            </div>
                          </div>
                          
                          <div className="text-center">
                            <p className="text-white/70 text-xs mb-1">المتبقي</p>
                            <p className="text-white font-bold text-sm">
                              {card.remaining.length > 10 ? card.remaining.substring(0, 10) + '...' : card.remaining}
                            </p>
                          </div>
                        </div>
                        
                        {/* Right - Performance */}
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          {card.trend === 'up' ? (
                            <ArrowTrendingUpIcon className="w-4 h-4 text-green-300" />
                          ) : card.trend === 'down' ? (
                            <ArrowTrendingDownIcon className="w-4 h-4 text-red-300" />
                          ) : (
                            <ArrowRightIcon className="w-4 h-4 text-white/60" />
                          )}
                          <span className="text-white/80 text-xs">{card.trendValue}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                  
                  {/* Expanded Details */}
                  <AnimatePresence>
                    {isSelected && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, y: -10 }}
                        animate={{ opacity: 1, height: 'auto', y: 0 }}
                        exit={{ opacity: 0, height: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        className="mt-3 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                      >
                        <div className="p-4">
                          <h5 className="text-base font-bold text-gray-900 dark:text-white mb-3 flex items-center">
                            <LightBulbIcon className="w-4 h-4 text-yellow-500 ml-2 rtl:ml-0 rtl:mr-2" />
                            التوصيات الذكية
                          </h5>
                          
                          <div className="space-y-2">
                            {card.customRecommendations?.slice(0, 2).map((recommendation: any, idx: number) => (
                              <motion.div
                                key={idx}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                className="flex items-start space-x-3 rtl:space-x-reverse p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                              >
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                  <LightBulbIcon className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <h6 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                                    {recommendation.title}
                                  </h6>
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 leading-relaxed">
                                    {recommendation.description}
                                  </p>
                                  
                                  {recommendation.savings && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-medium text-green-600 dark:text-green-400">
                                        توفير متوقع: {recommendation.savings}
                                      </span>
                                      <button 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRecommendationAction(recommendation.type, card.id);
                                        }}
                                        className="px-2 py-1 text-xs font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-md transition-colors"
                                      >
                                        تفعيل
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            ))}
                          </div>
                          
                          <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-600 mt-3">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewAllRecommendations(card.id);
                              }}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                            >
                              عرض جميع التوصيات
                            </button>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRefreshData(card.id);
                              }}
                              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 font-medium"
                            >
                              تحديث البيانات
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
          
          {/* View All Cards Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-6 text-center"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleViewAllCards}
              className="inline-flex items-center space-x-2 rtl:space-x-reverse px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg border border-gray-200 dark:border-gray-600"
            >
              <EyeIcon className="h-4 w-4" />
              <span>عرض جميع البطاقات ({smartCardsData.length})</span>
              <ArrowRightIcon className="h-4 w-4" />
            </motion.button>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default SmartCardsPreview;
